// WA tutoring landing page configuration.
// One entry per anchor suburb (≥7km separation) used for SEO landing pages
// served at /tutoring/{slug}. Slugs match franchise_locations.territory_slug.

export type WaSuburbConfig = {
  slug: string;             // e.g. 'subiaco-wa'
  name: string;             // 'Subiaco'
  region: string;           // 'Perth Western Suburbs'
  postcode: string;
  lat: number;
  lng: number;
  blurb: string;            // 1-2 sentence local context for hero
  nearby: { name: string; distance: string }[];
  // Local schools / context phrases woven into copy and FAQ for relevance
  primarySchools: string[];
  highSchools: string[];
  // Audience / demand framing
  parentIntent: string;     // e.g. 'PEAC and academic enrichment'
};

export const WA_TUTORING_SUBURBS: WaSuburbConfig[] = [
  {
    slug: 'subiaco-wa',
    name: 'Subiaco',
    region: 'Perth Western Suburbs',
    postcode: '6008',
    lat: -31.9489,
    lng: 115.8253,
    blurb:
      'Tutoring for Subiaco families chasing PEAC selection, scholarship offers and consistent top-of-class results.',
    nearby: [
      { name: 'Shenton Park', distance: '2km' },
      { name: 'Daglish', distance: '1km' },
      { name: 'Jolimont', distance: '2km' },
      { name: 'West Leederville', distance: '2km' },
      { name: 'Nedlands', distance: '4km' },
    ],
    primarySchools: ['Subiaco Primary School', 'Jolimont Primary School', 'Rosalie Primary School'],
    highSchools: ['Shenton College', 'Bob Hawke College'],
    parentIntent: 'PEAC preparation and academic extension',
  },
  {
    slug: 'cottesloe-wa',
    name: 'Cottesloe',
    region: 'Perth Western Suburbs',
    postcode: '6011',
    lat: -32.0014,
    lng: 115.7581,
    blurb:
      'Specialist tutoring for Cottesloe students preparing for scholarship exams, PEAC selection and private school entry.',
    nearby: [
      { name: 'Mosman Park', distance: '2km' },
      { name: 'Peppermint Grove', distance: '1km' },
      { name: 'Swanbourne', distance: '2km' },
      { name: 'Claremont', distance: '3km' },
      { name: 'North Fremantle', distance: '4km' },
    ],
    primarySchools: ['Cottesloe Primary School', 'North Cottesloe Primary School'],
    highSchools: ['Shenton College', 'John Curtin College of the Arts'],
    parentIntent: 'scholarship and private school entrance preparation',
  },
  {
    slug: 'scarborough-wa',
    name: 'Scarborough',
    region: 'Perth Inner West',
    postcode: '6019',
    lat: -31.8954,
    lng: 115.7585,
    blurb:
      'After-school tutoring and exam preparation for Scarborough families across maths, English and PEAC selection.',
    nearby: [
      { name: 'Doubleview', distance: '2km' },
      { name: 'Karrinyup', distance: '3km' },
      { name: 'Trigg', distance: '2km' },
      { name: 'Innaloo', distance: '3km' },
      { name: 'Wembley Downs', distance: '4km' },
    ],
    primarySchools: ['Scarborough Primary School', 'Deanmore Primary School'],
    highSchools: ['Churchlands Senior High School', 'Carine Senior High School'],
    parentIntent: 'PEAC and gifted-and-talented selection',
  },
  {
    slug: 'morley-wa',
    name: 'Morley',
    region: 'Perth Inner North',
    postcode: '6062',
    lat: -31.8884,
    lng: 115.9027,
    blurb:
      'Tutoring for Morley students focused on consistent school results, NAPLAN improvement and selective entry pathways.',
    nearby: [
      { name: 'Noranda', distance: '3km' },
      { name: 'Bayswater', distance: '3km' },
      { name: 'Dianella', distance: '3km' },
      { name: 'Embleton', distance: '2km' },
      { name: 'Bedford', distance: '2km' },
    ],
    primarySchools: ['Morley Primary School', 'Weld Square Primary School', 'Embleton Primary School'],
    highSchools: ['Morley Senior High School', 'John Forrest Secondary College'],
    parentIntent: 'NAPLAN improvement and academic extension',
  },
  {
    slug: 'joondalup-wa',
    name: 'Joondalup',
    region: 'Perth Northern Growth Corridor',
    postcode: '6027',
    lat: -31.7448,
    lng: 115.7661,
    blurb:
      'Year 1-10 tutoring in Joondalup, with PEAC preparation, NAPLAN coaching and weekly maths and English programmes.',
    nearby: [
      { name: 'Edgewater', distance: '2km' },
      { name: 'Heathridge', distance: '3km' },
      { name: 'Connolly', distance: '4km' },
      { name: 'Currambine', distance: '3km' },
      { name: 'Kinross', distance: '4km' },
    ],
    primarySchools: ['Joondalup Primary School', 'Edgewater Primary School', 'Heathridge Primary School'],
    highSchools: ['Belridge Secondary College', 'Ocean Reef Senior High School'],
    parentIntent: 'PEAC and NAPLAN preparation',
  },
  {
    slug: 'wanneroo-wa',
    name: 'Wanneroo',
    region: 'Perth Northern Growth Corridor',
    postcode: '6065',
    lat: -31.7497,
    lng: 115.8053,
    blurb:
      'Local tutoring in Wanneroo for primary and secondary students, with structured weekly maths and English programmes.',
    nearby: [
      { name: 'Pearsall', distance: '4km' },
      { name: 'Hocking', distance: '3km' },
      { name: 'Wangara', distance: '3km' },
      { name: 'Madeley', distance: '3km' },
      { name: 'Sinagra', distance: '3km' },
    ],
    primarySchools: ['Wanneroo Primary School', 'Pearsall Primary School', 'Hocking Primary School'],
    highSchools: ['Wanneroo Secondary College', 'Mindarie Senior College'],
    parentIntent: 'foundation skills and exam preparation',
  },
  {
    slug: 'tapping-wa',
    name: 'Tapping',
    region: 'Perth Northern Growth Corridor',
    postcode: '6065',
    lat: -31.7237,
    lng: 115.7944,
    blurb:
      'Tutoring for Tapping families: small-group maths, English and PEAC coaching designed around the WA curriculum.',
    nearby: [
      { name: 'Carramar', distance: '2km' },
      { name: 'Banksia Grove', distance: '4km' },
      { name: 'Ashby', distance: '3km' },
      { name: 'Tamala Park', distance: '5km' },
      { name: 'Mariginiup', distance: '5km' },
    ],
    primarySchools: ['Tapping Primary School', 'Spring Hill Primary School', 'Carramar Primary School'],
    highSchools: ['Joseph Banks Secondary College', 'Mindarie Senior College'],
    parentIntent: 'PEAC and primary literacy and numeracy support',
  },
  {
    slug: 'landsdale-wa',
    name: 'Landsdale',
    region: 'Perth Northern Growth Corridor',
    postcode: '6065',
    lat: -31.8141,
    lng: 115.8447,
    blurb:
      'Landsdale tutoring centre focus areas: maths fluency, written English, and pathways into selective and PEAC programmes.',
    nearby: [
      { name: 'Madeley', distance: '2km' },
      { name: 'Darch', distance: '2km' },
      { name: 'Kingsway', distance: '3km' },
      { name: 'Wangara', distance: '3km' },
      { name: 'Alexander Heights', distance: '3km' },
    ],
    primarySchools: ['Landsdale Primary School', 'Madeley Primary School', 'Ashdale Primary School'],
    highSchools: ['Ashdale Secondary College', 'Wanneroo Secondary College'],
    parentIntent: 'PEAC selection and academic enrichment',
  },
  {
    slug: 'burns-beach-wa',
    name: 'Burns Beach',
    region: 'Perth Northern Growth Corridor',
    postcode: '6028',
    lat: -31.7211,
    lng: 115.7158,
    blurb:
      'Tutoring for Burns Beach and Iluka students, including PEAC preparation and consistent maths and English support.',
    nearby: [
      { name: 'Iluka', distance: '1km' },
      { name: 'Currambine', distance: '3km' },
      { name: 'Kinross', distance: '3km' },
      { name: 'Mindarie', distance: '5km' },
      { name: 'Ocean Reef', distance: '4km' },
    ],
    primarySchools: ['Burns Beach Primary School', 'Currambine Primary School'],
    highSchools: ['Ocean Reef Senior High School', 'Mindarie Senior College'],
    parentIntent: 'PEAC preparation and academic extension',
  },
  {
    slug: 'yanchep-wa',
    name: 'Yanchep',
    region: 'Perth Far North',
    postcode: '6035',
    lat: -31.5469,
    lng: 115.6322,
    blurb:
      'Tutoring for Yanchep families: weekly small-group maths and English programmes plus NAPLAN preparation.',
    nearby: [
      { name: 'Two Rocks', distance: '5km' },
      { name: 'Eglinton', distance: '6km' },
      { name: 'Alkimos', distance: '8km' },
      { name: 'Butler', distance: '10km' },
    ],
    primarySchools: ['Yanchep Beach Primary School', 'Yanchep Rise Primary School'],
    highSchools: ['Yanchep Secondary College'],
    parentIntent: 'foundation skills and NAPLAN coaching',
  },
  {
    slug: 'two-rocks-wa',
    name: 'Two Rocks',
    region: 'Perth Far North',
    postcode: '6037',
    lat: -31.4901,
    lng: 115.5856,
    blurb:
      'Two Rocks tutoring: structured weekly programmes that build the skills WA primary and secondary students need to thrive.',
    nearby: [
      { name: 'Yanchep', distance: '5km' },
      { name: 'Eglinton', distance: '10km' },
      { name: 'Alkimos', distance: '12km' },
    ],
    primarySchools: ['Atlantis Beach Baptist College (Primary)', 'Yanchep Beach Primary School'],
    highSchools: ['Yanchep Secondary College', 'Atlantis Beach Baptist College'],
    parentIntent: 'NAPLAN and primary skill building',
  },
  {
    slug: 'ellenbrook-wa',
    name: 'Ellenbrook',
    region: 'Perth Eastern Growth Corridor',
    postcode: '6069',
    lat: -31.7672,
    lng: 116.0247,
    blurb:
      'Ellenbrook tutoring across the Swan Valley: PEAC preparation, weekly maths and English, and Year 7-10 extension.',
    nearby: [
      { name: 'Aveley', distance: '2km' },
      { name: 'Brabham', distance: '3km' },
      { name: 'Henley Brook', distance: '4km' },
      { name: 'Dayton', distance: '5km' },
      { name: 'Caversham', distance: '6km' },
    ],
    primarySchools: ['Ellenbrook Primary School', 'Aveley Primary School', 'Malvern Springs Primary School'],
    highSchools: ['Ellenbrook Secondary College', 'Aveley Secondary College'],
    parentIntent: 'PEAC preparation and academic extension',
  },
  {
    slug: 'midland-wa',
    name: 'Midland',
    region: 'Perth Eastern Growth Corridor',
    postcode: '6056',
    lat: -31.8902,
    lng: 116.0107,
    blurb:
      'Midland tutoring for families across Midvale, Viveash and Woodbridge: maths, English and exam prep tutoring.',
    nearby: [
      { name: 'Midvale', distance: '2km' },
      { name: 'Viveash', distance: '2km' },
      { name: 'Woodbridge', distance: '2km' },
      { name: 'Bellevue', distance: '2km' },
      { name: 'Guildford', distance: '4km' },
    ],
    primarySchools: ['Midvale Primary School', 'Woodbridge Primary School', 'Guildford Primary School'],
    highSchools: ['Governor Stirling Senior High School', 'La Salle College'],
    parentIntent: 'NAPLAN, maths and English support',
  },
  {
    slug: 'applecross-wa',
    name: 'Applecross',
    region: 'Perth South of River',
    postcode: '6153',
    lat: -32.0167,
    lng: 115.8333,
    blurb:
      'Applecross tutoring for high-achieving riverside families targeting PEAC, scholarships and Applecross SHS extension.',
    nearby: [
      { name: 'Ardross', distance: '2km' },
      { name: 'Mount Pleasant', distance: '2km' },
      { name: 'Brentwood', distance: '3km' },
      { name: 'Booragoon', distance: '3km' },
      { name: 'Como', distance: '4km' },
    ],
    primarySchools: ['Applecross Primary School', 'Ardross Primary School', 'Mount Pleasant Primary School'],
    highSchools: ['Applecross Senior High School', 'Rossmoyne Senior High School'],
    parentIntent: 'PEAC, scholarship and Applecross SHS extension',
  },
  {
    slug: 'canning-vale-wa',
    name: 'Canning Vale',
    region: 'Perth South-East',
    postcode: '6155',
    lat: -32.0644,
    lng: 115.9242,
    blurb:
      'Canning Vale tutoring for Rossmoyne, Willetton and PEAC-bound students focused on top-percentile results.',
    nearby: [
      { name: 'Willetton', distance: '4km' },
      { name: 'Thornlie', distance: '4km' },
      { name: 'Southern River', distance: '3km' },
      { name: 'Harrisdale', distance: '4km' },
      { name: 'Piara Waters', distance: '5km' },
    ],
    primarySchools: ['Canning Vale Primary School', 'Caladenia Primary School', 'Campbell Primary School'],
    highSchools: ['Canning Vale College', 'Willetton Senior High School', 'Rossmoyne Senior High School'],
    parentIntent: 'PEAC, Rossmoyne and Willetton extension',
  },
  {
    slug: 'kalamunda-wa',
    name: 'Kalamunda',
    region: 'Perth South-East',
    postcode: '6076',
    lat: -31.9747,
    lng: 116.0581,
    blurb:
      'Kalamunda tutoring in the Hills: weekly maths and English plus PEAC and academic extension for primary and secondary students.',
    nearby: [
      { name: 'Lesmurdie', distance: '2km' },
      { name: 'Forrestfield', distance: '5km' },
      { name: 'High Wycombe', distance: '4km' },
      { name: 'Maida Vale', distance: '3km' },
      { name: 'Walliston', distance: '3km' },
    ],
    primarySchools: ['Kalamunda Primary School', 'Lesmurdie Primary School', 'Walliston Primary School'],
    highSchools: ['Kalamunda Senior High School', 'Lesmurdie Senior High School'],
    parentIntent: 'PEAC and high-school extension',
  },
  {
    slug: 'baldivis-wa',
    name: 'Baldivis',
    region: 'Perth Southern Growth Corridor',
    postcode: '6171',
    lat: -32.3367,
    lng: 115.8019,
    blurb:
      'Baldivis tutoring: weekly small-group maths and English, NAPLAN coaching and Year 7-10 extension programmes.',
    nearby: [
      { name: 'Bertram', distance: '4km' },
      { name: 'Wellard', distance: '6km' },
      { name: 'Warnbro', distance: '5km' },
      { name: 'Port Kennedy', distance: '5km' },
      { name: 'Secret Harbour', distance: '8km' },
    ],
    primarySchools: ['Baldivis Primary School', 'Tuart Rise Primary School', 'Settlers Primary School'],
    highSchools: ['Baldivis Secondary College', 'Comet Bay College'],
    parentIntent: 'NAPLAN and curriculum support',
  },
  {
    slug: 'byford-wa',
    name: 'Byford',
    region: 'Perth Southern Growth Corridor',
    postcode: '6122',
    lat: -32.2167,
    lng: 116.0,
    blurb:
      'Byford tutoring for growing families south of Perth: foundation skills, NAPLAN and weekly maths and English programmes.',
    nearby: [
      { name: 'Mundijong', distance: '3km' },
      { name: 'Cardup', distance: '3km' },
      { name: 'Serpentine', distance: '8km' },
      { name: 'Armadale', distance: '7km' },
      { name: 'Darling Downs', distance: '4km' },
    ],
    primarySchools: ['Byford Primary School', 'West Byford Primary School', 'Salvado Catholic College (Primary)'],
    highSchools: ['Byford Secondary College', 'Salvado Catholic College'],
    parentIntent: 'foundation skills and NAPLAN preparation',
  },
  {
    slug: 'wellard-wa',
    name: 'Wellard',
    region: 'Perth Southern Growth Corridor',
    postcode: '6170',
    lat: -32.2706,
    lng: 115.8156,
    blurb:
      'Wellard tutoring for primary and secondary students: structured weekly programmes aligned to the WA curriculum.',
    nearby: [
      { name: 'Bertram', distance: '2km' },
      { name: 'Leda', distance: '3km' },
      { name: 'Kwinana', distance: '4km' },
      { name: 'Baldivis', distance: '6km' },
      { name: 'Orelia', distance: '5km' },
    ],
    primarySchools: ['Wellard Primary School', 'Bertram Primary School', 'Leda Primary School'],
    highSchools: ['Gilmore College', 'Baldivis Secondary College'],
    parentIntent: 'NAPLAN and weekly skill building',
  },
  {
    slug: 'atwell-wa',
    name: 'Atwell',
    region: 'Perth Southern Growth Corridor',
    postcode: '6164',
    lat: -32.1483,
    lng: 115.8597,
    blurb:
      'Atwell tutoring: PEAC preparation, weekly maths and English, and Year 7-10 extension for the south-west corridor.',
    nearby: [
      { name: 'Aubin Grove', distance: '2km' },
      { name: 'Success', distance: '2km' },
      { name: 'Hammond Park', distance: '2km' },
      { name: 'Jandakot', distance: '3km' },
      { name: 'Beeliar', distance: '4km' },
    ],
    primarySchools: ['Atwell Primary School', 'Aubin Grove Primary School', 'Hammond Park Primary School'],
    highSchools: ['Atwell College', 'Hammond Park Secondary College'],
    parentIntent: 'PEAC and academic extension',
  },
];

export const getWaSuburb = (slug: string) =>
  WA_TUTORING_SUBURBS.find((s) => s.slug === slug);
